import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
    ArrowLeft,
    Banknote,
    Check,
    Clock,
    CreditCard,
    DollarSign,
    Printer,
    QrCode,
    Receipt,
    ShoppingCart,
    Tag,
    Undo2,
    XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import { cancelSale, getSaleResumeFinalAmount, hasNonZeroProductItemDiscount } from '../../common/saleUtils';
import DialogForm from '../../component/dialog/DialogForm';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileSaleDetail from '../../component/sale/MobileSaleDetail';

// Ícone por status (header + pill translúcido no gradiente) — mesmos 4
// status reais de venda usados em `sales/List.js`/`StatusChip domain="sale"`
// (DESIGN-SYSTEM.md §4), mapeados aos ícones do `prototipo-visualizacao-venda.html`.
const STATUS_ICON = {
    iniciada: Clock,
    finalizada: Check,
    cancelada: XCircle,
    estornada: Undo2,
};

function normalizeStatus(status) {
    return (status || '').trim().toLowerCase();
}

// Pill de status sobre o cabeçalho em gradiente — não reaproveita
// `<StatusChip domain="sale">` porque o bg tingido dessa última (§4) fica
// ilegível sobre o gradiente de marca; aqui o pill é sempre translúcido
// branco, exatamente como `#saleStatusPill` no protótipo.
function HeaderStatusPill({ status }) {
    const Icon = STATUS_ICON[normalizeStatus(status)] || Receipt;
    return (
        <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '11.5px',
            fontWeight: 700,
            padding: '5px 11px',
            borderRadius: '20px',
            bgcolor: 'rgba(255,255,255,.22)',
            color: '#fff',
        }}>
            <Icon size={13} strokeWidth={2.5} />
            {status}
        </Box>
    );
}

// Botão de ação do header (`.sale-actions button`) — ghost translúcido sobre
// o gradiente, com variante de hover destrutivo para "Cancelar venda".
function HeaderActionButton({ icon: Icon, label, onClick, danger }) {
    return (
        <Button
            onClick={onClick}
            startIcon={<Icon size={14} strokeWidth={2} />}
            sx={{
                padding: '9px 14px',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,.3)',
                bgcolor: 'rgba(255,255,255,.12)',
                color: '#fff',
                fontSize: '12.5px',
                fontWeight: 700,
                '&:hover': danger
                    ? { bgcolor: '#fecaca', color: '#7f1d1d', borderColor: '#fecaca' }
                    : { bgcolor: 'rgba(255,255,255,.22)', borderColor: 'rgba(255,255,255,.3)' },
            }}
        >
            {label}
        </Button>
    );
}

// Par label/valor do `.sale-meta-grid` (Data da venda / Vendedor / Caixa / Itens).
function MetaItem({ label, value, loading }) {
    return (
        <Box>
            <Typography sx={{ fontSize: '10.5px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px', color: '#fff' }}>
                {label}
            </Typography>
            {loading ? (
                <Skeleton variant="text" width={90} height={20} sx={{ bgcolor: 'rgba(255,255,255,.3)' }} />
            ) : (
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#fff' }}>{value}</Typography>
            )}
        </Box>
    );
}

// `.alert-danger` — motivo do cancelamento.
function CancelReasonBanner({ reason }) {
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px 16px',
            borderRadius: '14px',
            mb: 2.25,
            bgcolor: 'error.bg',
            border: '1px solid',
            borderColor: 'error.main',
        }}>
            <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: 'error.main',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}>
                <XCircle size={18} strokeWidth={2} />
            </Box>
            <Box>
                <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'error.main', mb: '2px' }}>
                    Motivo do cancelamento
                </Typography>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'error.main' }}>
                    {reason}
                </Typography>
            </Box>
        </Box>
    );
}

// Card-head (`.card-head`) reaproveitado nos 3 cards de conteúdo (Resumo
// Financeiro / Pagamentos / Produtos) — DESIGN-SYSTEM.md §5.2.
function CardHeadRow({ icon: Icon, iconBg, iconFg, title, subtitle, loading }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
            <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: iconBg,
                color: iconFg,
            }}>
                <Icon size={18} strokeWidth={2} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: '1px' }}>
                    {loading ? <Skeleton variant="text" width={130} /> : subtitle}
                </Typography>
            </Box>
        </Box>
    );
}

// `.summary-row` — usado nos painéis Valores/Impostos do resumo financeiro.
function FinRow({ icon: Icon, iconColor, label, value, valueColor = 'text.primary', valueWeight = 700, valueSize = '15px', loading }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.875, gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {Icon && <Icon size={13} strokeWidth={2} color={iconColor} />}
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{label}</Typography>
            </Box>
            {loading ? (
                <Skeleton variant="text" width={70} height={20} />
            ) : (
                <Typography sx={{ fontSize: valueSize, fontWeight: valueWeight, color: valueColor }}>{value}</Typography>
            )}
        </Box>
    );
}

// `.paid-row` — uma forma de pagamento registrada na venda.
const PAYMENT_META = {
    'CRÉDITO': { icon: CreditCard, iconBg: 'secondary.bg', iconFg: 'secondary.main', label: 'Crédito' },
    'DÉBITO': { icon: CreditCard, iconBg: 'info.bg', iconFg: 'primary.main', label: 'Débito' },
    'PIXKEY': { icon: QrCode, iconBg: 'info.bg', iconFg: 'brand2.main', label: 'PIX' },
    'PIXQRCODE': { icon: QrCode, iconBg: 'info.bg', iconFg: 'brand2.main', label: 'PIX QRCode' },
    'DINHEIRO': { icon: Banknote, iconBg: 'success.bg', iconFg: 'success.main', label: 'Dinheiro' },
};

function PaymentRow({ payment }) {
    const meta = PAYMENT_META[payment.method] || { icon: CreditCard, iconBg: 'background.subtle', iconFg: 'text.disabled', label: payment.method };
    const Icon = meta.icon;

    let extra = null;
    if (payment.method === 'CRÉDITO' && payment.installment) {
        extra = payment.number_installment === 1
            ? `À vista · taxa ${payment.tax_machine}% · recebido R$ ${payment.discount_tax_card_machine}`
            : `${payment.number_installment}x de R$ ${payment.installment} · taxa ${payment.tax_machine}% · recebido R$ ${payment.discount_tax_card_machine}`;
    } else if (payment.method === 'DÉBITO') {
        extra = `Taxa ${payment.tax_machine}% · recebido R$ ${payment.discount_tax_card_machine}`;
    } else if (payment.method === 'DINHEIRO' && payment.cash_change && payment.cash_change !== '0,00') {
        extra = `Troco: R$ ${payment.cash_change}`;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, padding: '13px', bgcolor: 'background.subtle', borderRadius: '12px', mb: 1, '&:last-child': { mb: 0 } }}>
            <Box sx={{
                width: 34,
                height: 34,
                borderRadius: '9px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: meta.iconBg,
                color: meta.iconFg,
            }}>
                <Icon size={16} strokeWidth={2} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{meta.label}</Typography>
                {extra && (
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '1px' }}>{extra}</Typography>
                )}
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '13.5px', color: 'text.primary', flexShrink: 0, ml: 'auto' }}>
                R$ {payment.amount}
            </Typography>
        </Box>
    );
}

// `.fin-empty`/`.items-empty` — DESIGN-SYSTEM.md §5.11.
function EmptyMiniState({ icon: Icon, title, subtitle, size = 56 }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3.5, textAlign: 'center' }}>
            <Box sx={{
                width: size,
                height: size,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
            }}>
                <Icon size={size === 56 ? 22 : 26} strokeWidth={1.6} />
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>}
        </Box>
    );
}

export default function View() {
    const theme = useTheme();
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();
    const [sale, setSale] = useState({
        number: '',
        status: '',
        date: '',
        user: '',
        cash_register_name: '',
        reason_cancel: '',
        total_amount: 0,
        total_discount: '',
        total_discount_applied: '',
        total_percent_tax: '',
        total_tax_machine: '',
        total_discount_tax: '',
        items_gross_subtotal: '',
        total_item_discount: '',
        net_after_item_discounts: '',
        total_discounts_combined: '',
        final_total: '',
    });
    const [loading, setLoading] = useState(true);
    const [saleItems, setSaleItems] = useState([]);
    const [salePayments, setSalePayments] = useState([]);
    const [openPrintDialog, setOpenPrintDialog] = useState(false);
    const [printProgress, setPrintProgress] = useState(0);
    const [openDialogReverseSale, setOpenDialogReverseSale] = useState(false);
    const [openDialogCancelSale, setOpenDialogCancelSale] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [loadingCancel, setLoadingCancel] = useState(false);
    const [totalSaleItems, setTotalSaleItems] = useState(0);
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [messageSnackBar, setMessageSnackBar] = useState('');

    const fetchSale = async () => {
        try {
            const response = await httpClient.get(`/sales/${id}/resume`);
            setSale(response.data);
        } catch (error) {
            console.error('Erro ao buscar dados da venda:', error);
        }
    }

    const fetchSaleItems = async (id) => {
        try {
            const response = await httpClient.get(`/sales/${id}/items`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar itens da venda:', error);
            return [];
        }
    }

    const fetchSalePayments = async (id) => {
        try {
            const response = await httpClient.get(`/sales/${id}/payments`);
            setSalePayments(response.data);
        } catch (error) {
            console.error('Erro ao buscar pagamentos da venda:', error);
            setSalePayments([]);
        }
    }

    const print = async () => {
        const response = await httpClient.post(`/sales/print?saleId=${id}`);
        return response;
    }

    const handleReverseSale = async () => {
        // Mesmo estado `loading` da carga inicial da página (não um estado
        // dedicado) — comportamento idêntico ao arquivo anterior, preservado
        // aqui sem alteração.
        setLoading(true);
        try {
            await httpClient.post(`/sales/reverse/${id}`);
            setOpenDialogReverseSale(false);
            setSale(prev => ({ ...prev, status: 'Estornada' }));
            setMessageSnackBar('Venda estornada com sucesso');
            setOpenSnackBar(true);
        } catch (error) {
            console.error('Erro ao estornar venda:', error);
            setMessageSnackBar('Erro ao estornar venda');
            setOpenSnackBar(true);
        } finally {
            setLoading(false);
        }
    }

    const handleCancelSale = async () => {
        if (!cancelReason.trim()) {
            return;
        }

        setLoadingCancel(true);
        try {
            await cancelSale(id, cancelReason);
            setOpenDialogCancelSale(false);
            setSale(prev => ({ ...prev, status: 'Cancelada', reason_cancel: cancelReason }));
            setMessageSnackBar('Venda cancelada com sucesso');
            setOpenSnackBar(true);
            setCancelReason('');
        } catch (error) {
            console.error('Erro ao cancelar venda:', error);
            setMessageSnackBar('Erro ao cancelar venda');
            setOpenSnackBar(true);
        } finally {
            setLoadingCancel(false);
        }
    }

    const handlePrint = async () => {
        setOpenPrintDialog(true);
        setPrintProgress(0);

        // Progresso fake (não reflete upload real) — mantém exatamente o
        // mesmo comportamento/tempo da versão anterior desta tela.
        const interval = setInterval(() => {
            setPrintProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    return 100;
                }
                return prev + 15;
            });
        }, 50);

        setTimeout(() => {
            clearInterval(interval);
            setPrintProgress(100);
            setOpenPrintDialog(false);
            print().then(response => {
                if (response.status !== 200) {
                    setMessageSnackBar('Erro ao imprimir comprovante');
                    setOpenSnackBar(true);
                }
            });
        }, 1000);
    }

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                await Promise.all([
                    fetchSale(),
                    fetchSaleItems(id).then(response => {
                        setSaleItems(response);
                        let count = 0;
                        response.forEach(item => {
                            count += item.quantity;
                        });
                        setTotalSaleItems(count);
                    }),
                    fetchSalePayments(id)
                ]);
            } catch (error) {
                console.error('Erro ao carregar dados:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id]);

    const breadcrumbs = [
        { title: 'Vendas', link: '/sales' },
    ]

    // Ações do header computadas por status — DESIGN-SYSTEM.md §6.3 (lookup,
    // não uma cadeia de condicionais espalhada pelo render). Mesmas regras já
    // usadas em `sales/List.js`: Cancelar só em "Iniciada", Estornar só em
    // "Finalizada", ambas atrás da respectiva permissão.
    const statusKey = normalizeStatus(sale.status);
    const canCancel = statusKey === 'iniciada' && hasPermission('sale.cancel');
    const canReverse = statusKey === 'finalizada' && hasPermission('sale.reverse');

    const headerActions = loading ? [] : [
        { key: 'print', label: 'Imprimir', icon: Printer, onClick: handlePrint },
        ...(canCancel ? [{ key: 'cancel', label: 'Cancelar venda', icon: XCircle, danger: true, onClick: () => setOpenDialogCancelSale(true) }] : []),
        ...(canReverse ? [{ key: 'reverse', label: 'Estornar venda', icon: Undo2, onClick: () => setOpenDialogReverseSale(true) }] : []),
    ];

    // Mobile: o detalhe usa `MobileSaleDetail` (protótipo `vendas/detalhe.html`), que registra
    // a ação "Ações" no AppBar; as ações seguem as mesmas condições (`canCancel`/`canReverse`).
    const canContinue = statusKey === 'iniciada' && hasPermission('sale.create');

    return (
        <PageScaffold
            links={breadcrumbs}
            titlePageTarget={'Visualizar'}
            message={messageSnackBar}
            openSnackbar={openSnackBar}
            setOpenSnackbar={setOpenSnackBar}
            severity={messageSnackBar.includes('Erro') ? 'error' : 'success'}
        >
            {isMobileShell ? (
                <MobileSaleDetail
                    sale={sale}
                    items={saleItems}
                    payments={salePayments}
                    loading={loading}
                    canCancel={canCancel}
                    canReverse={canReverse}
                    canContinue={canContinue}
                    loadingAction={loadingCancel}
                    onPrint={handlePrint}
                    onContinue={() => navigate(`/sale?id=${id}`)}
                    cancelOpen={openDialogCancelSale}
                    onOpenCancel={() => setOpenDialogCancelSale(true)}
                    onCloseCancel={() => {
                        setOpenDialogCancelSale(false);
                        setCancelReason('');
                    }}
                    cancelReason={cancelReason}
                    onCancelReasonChange={setCancelReason}
                    onConfirmCancel={handleCancelSale}
                    reverseOpen={openDialogReverseSale}
                    onOpenReverse={() => setOpenDialogReverseSale(true)}
                    onCloseReverse={() => setOpenDialogReverseSale(false)}
                    onConfirmReverse={handleReverseSale}
                />
            ) : (
            <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto' }}>
                {/* HEADER — sale-header (gradiente de marca, §2.4/§5.2) */}
                <Card elevation={0} sx={{
                    borderRadius: '20px',
                    mb: 2.25,
                    padding: '22px 24px',
                    background: theme.palette.gradient.brand,
                    boxShadow: theme.customShadows.md,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            {!isMobileShell && <Tooltip title="Voltar">
                                <IconButton
                                    onClick={() => navigate(-1)}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '10px',
                                        bgcolor: 'rgba(255,255,255,.15)',
                                        border: '1px solid rgba(255,255,255,.25)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,.25)' },
                                    }}
                                >
                                    <ArrowLeft size={16} strokeWidth={2} />
                                </IconButton>
                            </Tooltip>}
                            {loading ? (
                                <Skeleton variant="text" width={180} height={34} sx={{ bgcolor: 'rgba(255,255,255,.3)' }} />
                            ) : (
                                <Typography component="h1" sx={{ fontSize: '21px', fontWeight: 800, letterSpacing: '-.01em', color: '#fff' }}>
                                    Venda Nº {sale.number}
                                </Typography>
                            )}
                            {!loading && <HeaderStatusPill status={sale.status} />}
                        </Box>

                        {!isMobileShell && headerActions.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {headerActions.map(({ key, label, icon, onClick, danger }) => (
                                    <HeaderActionButton key={key} icon={icon} label={label} danger={danger} onClick={onClick} />
                                ))}
                            </Box>
                        )}
                    </Box>

                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' },
                        gap: 2.25,
                    }}>
                        <MetaItem label="Data da venda" value={sale.date} loading={loading} />
                        <MetaItem label="Vendedor" value={sale.user} loading={loading} />
                        <MetaItem label="Cliente" value={sale.customer_name || '—'} loading={loading} />
                        <MetaItem label="Caixa" value={sale.cash_register_name || '—'} loading={loading} />
                        <MetaItem
                            label="Itens"
                            value={`${totalSaleItems} ${totalSaleItems === 1 ? 'unidade' : 'unidades'}`}
                            loading={loading}
                        />
                    </Box>
                </Card>

                {/* MOTIVO DO CANCELAMENTO */}
                {!loading && sale.status === 'Cancelada' && sale.reason_cancel && (
                    <CancelReasonBanner reason={sale.reason_cancel} />
                )}

                {/* RESUMO FINANCEIRO + PAGAMENTOS */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.25, mb: 2.25 }}>
                    <Card elevation={0}>
                        <CardContent sx={{ p: 2.25 }}>
                            <CardHeadRow
                                icon={DollarSign}
                                iconBg="success.bg"
                                iconFg="success.main"
                                title="Resumo financeiro"
                                subtitle="Valores e impostos da venda"
                                loading={loading}
                            />
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                <Box sx={{ bgcolor: 'background.subtle', border: '1px solid', borderColor: 'divider', borderRadius: '12px', p: 2 }}>
                                    <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.secondary', mb: 1.25 }}>
                                        Valores
                                    </Typography>
                                    <FinRow
                                        label="Bruto (itens)"
                                        value={`R$ ${sale.items_gross_subtotal !== undefined && sale.items_gross_subtotal !== '' ? sale.items_gross_subtotal : sale.total_amount}`}
                                        loading={loading}
                                    />
                                    <FinRow
                                        icon={Tag}
                                        iconColor={theme.palette.error.main}
                                        label="Desconto nos produtos"
                                        value={`− R$ ${sale.total_item_discount !== undefined && sale.total_item_discount !== '' ? sale.total_item_discount : '0,00'}`}
                                        valueColor="error.main"
                                        loading={loading}
                                    />
                                    {!loading && hasNonZeroProductItemDiscount(sale) && (
                                        <FinRow
                                            label="Líquido após itens"
                                            value={`R$ ${sale.net_after_item_discounts !== undefined && sale.net_after_item_discounts !== '' ? sale.net_after_item_discounts : sale.total_amount}`}
                                        />
                                    )}
                                    <Divider sx={{ my: 0.5 }} />
                                    <FinRow
                                        icon={Tag}
                                        iconColor={theme.palette.error.main}
                                        label="Desconto na venda"
                                        value={`− R$ ${sale.total_discount_applied != null && sale.total_discount_applied !== '' ? sale.total_discount_applied : '0,00'}`}
                                        valueColor="error.main"
                                        loading={loading}
                                    />
                                    {!loading && sale.total_discounts_combined !== undefined && sale.total_discounts_combined !== '' && (
                                        <FinRow
                                            label="Total de descontos"
                                            value={`− R$ ${sale.total_discounts_combined}`}
                                            valueColor="error.main"
                                            valueWeight={600}
                                            valueSize="13px"
                                        />
                                    )}
                                    <Divider sx={{ my: 0.5 }} />
                                    <FinRow
                                        label="Valor final"
                                        value={`R$ ${getSaleResumeFinalAmount(sale)}`}
                                        valueColor="success.main"
                                        valueWeight={800}
                                        valueSize="17px"
                                        loading={loading}
                                    />
                                </Box>

                                <Box sx={{ bgcolor: 'background.subtle', border: '1px solid', borderColor: 'divider', borderRadius: '12px', p: 2 }}>
                                    <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.secondary', mb: 1.25 }}>
                                        Impostos
                                    </Typography>
                                    <FinRow
                                        label={`Taxa (${sale.total_percent_tax || '0'}%)`}
                                        value={`R$ ${sale.total_tax_machine || '0,00'}`}
                                        loading={loading}
                                    />
                                    <Divider sx={{ my: 0.5 }} />
                                    <FinRow
                                        label="Valor líquido"
                                        value={`R$ ${sale.total_discount_tax || '0,00'}`}
                                        valueWeight={800}
                                        valueSize="17px"
                                        loading={loading}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card elevation={0}>
                        <CardContent sx={{ p: 2.25 }}>
                            <CardHeadRow
                                icon={CreditCard}
                                iconBg="warning.bg"
                                iconFg="warning.main"
                                title="Pagamentos"
                                subtitle={
                                    loading ? '' : `${salePayments.length} ${salePayments.length === 1 ? 'forma de pagamento' : 'forma(s) de pagamento'}`
                                }
                                loading={loading}
                            />
                            {loading ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {[1, 2].map((item) => (
                                        <Skeleton key={item} variant="rounded" height={62} sx={{ borderRadius: '12px' }} />
                                    ))}
                                </Box>
                            ) : salePayments.length === 0 ? (
                                <EmptyMiniState icon={CreditCard} title="Nenhum pagamento registrado para esta venda." />
                            ) : (
                                <Box>
                                    {salePayments.map((payment, index) => (
                                        <PaymentRow key={index} payment={payment} />
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* PRODUTOS */}
                <Card elevation={0}>
                    <CardContent sx={{ p: 2.25 }}>
                        <CardHeadRow
                            icon={ShoppingCart}
                            iconBg="info.bg"
                            iconFg="primary.main"
                            title="Produtos"
                            subtitle={loading ? '' : `${totalSaleItems} unidades`}
                            loading={loading}
                        />
                        {loading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {[1, 2, 3].map((item) => (
                                    <Skeleton key={item} variant="rounded" height={48} />
                                ))}
                            </Box>
                        ) : saleItems.length === 0 ? (
                            <EmptyMiniState icon={ShoppingCart} title="Nenhum produto nesta venda" size={64} />
                        ) : (
                            <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {['Produto', 'Qtd', 'Preço Un.', 'Subtotal', 'Desconto', 'Total'].map((head, idx) => (
                                                <TableCell
                                                    key={head}
                                                    align={idx === 5 ? 'right' : 'left'}
                                                    sx={{
                                                        fontSize: '10.5px',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '.05em',
                                                        color: 'text.disabled',
                                                        fontWeight: 700,
                                                        border: 0,
                                                        borderBottom: '1px solid',
                                                        borderColor: 'divider',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {head}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {saleItems.map((item, index) => (
                                            <TableRow
                                                key={index}
                                                sx={{ '&:hover': { bgcolor: 'background.subtle' }, '&:last-child td': { borderBottom: 0 } }}
                                            >
                                                <TableCell sx={{ borderColor: 'divider' }}>
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{item.name}</Typography>
                                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '1px' }}>Código: {item.code || 'N/A'}</Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderColor: 'divider' }}>
                                                    <Box component="span" sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: 24,
                                                        padding: '2px 8px',
                                                        borderRadius: '20px',
                                                        bgcolor: 'info.bg',
                                                        color: 'primary.main',
                                                        fontWeight: 800,
                                                        fontSize: '12px',
                                                    }}>
                                                        {item.quantity}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '13px', color: 'text.secondary', borderColor: 'divider' }}>
                                                    R$ {item.price}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '13px', color: 'text.secondary', borderColor: 'divider' }}>
                                                    {item.subtotal != null && item.subtotal !== '' ? `R$ ${item.subtotal}` : '—'}
                                                </TableCell>
                                                <TableCell sx={{
                                                    fontSize: '13px',
                                                    borderColor: 'divider',
                                                    color: item.item_discount_amount ? 'error.main' : 'text.disabled',
                                                    fontWeight: item.item_discount_amount ? 600 : 400,
                                                }}>
                                                    {item.item_discount_amount ? `− R$ ${item.item_discount_amount}` : '—'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 800, fontSize: '13.5px', color: 'success.main', borderColor: 'divider' }}>
                                                    R$ {item.total}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>
            </Box>
            )}

            <Dialog
                open={openPrintDialog}
                onClose={() => setOpenPrintDialog(false)}
                aria-labelledby="print-dialog-title"
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle id="print-dialog-title" sx={{ fontSize: '16px', fontWeight: 800 }}>
                    Imprimindo Comprovante
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ width: '100%', mt: 1, mb: 2 }}>
                        <LinearProgress
                            variant="determinate"
                            value={printProgress}
                            sx={{
                                height: 9,
                                borderRadius: '6px',
                                bgcolor: 'background.subtle',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: '6px',
                                    backgroundImage: theme.palette.gradient.brand,
                                },
                            }}
                        />
                    </Box>
                </DialogContent>
            </Dialog>

            {!isMobileShell && (<>
            <DialogForm
                iconType="warning"
                title="Atenção"
                content="Deseja realmente estornar esta venda? Os pagamentos registrados permanecem para conferência."
                onSubmit={handleReverseSale}
                open={openDialogReverseSale}
                onClose={() => setOpenDialogReverseSale(false)}
                confirmButtonText="Confirmar"
                loading={loading}
                loadingText="Processando estorno..."
            />
            <DialogForm
                iconType="warning"
                title="Cancelar Venda"
                content="Deseja realmente cancelar esta venda? Informe o motivo do cancelamento."
                onSubmit={handleCancelSale}
                open={openDialogCancelSale}
                onClose={() => {
                    setOpenDialogCancelSale(false);
                    setCancelReason('');
                }}
                confirmButtonText="Confirmar Cancelamento"
                cancelButtonText="Cancelar"
                loading={loadingCancel}
                loadingText="Processando cancelamento..."
            >
                <TextField
                    label="Motivo do Cancelamento"
                    fullWidth
                    multiline
                    rows={3}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Digite o motivo do cancelamento..."
                    required
                    sx={{ mt: 2 }}
                />
            </DialogForm>
            </>)}
        </PageScaffold>
    );
}
